// Smart Views speichern und Filter-Abfragen (Runde 5, Agent 5A): Typen und Client nach crm/API.md, Abschnitt 20.1.
// Gerüst der Vorarbeit: die Routen antworten bis zum Bau durch 5A mit 501 `nicht_umgesetzt`. Neue Funktionen und
// optionale Felder ergänzt 5A hier additiv; bestehende Namen und Formen ändern sich nicht (5B und die Seiten hängen daran).
import { http } from "./client";
import type { LeadListe, SmartView, SmartViewFeld, SmartViewKurz } from "./types";

/** Abfrage im Close-Format wie in `SmartView.s_query`: {query: Baum, sort: […], results_limit}. Die Abfrage-Engine
 *  (api/crm_api/abfrage) liest genau diese Form. */
export type Abfrage = NonNullable<SmartView["s_query"]>;

export type FilterObjekt = "lead" | "contact" | "opportunity";

/** Noch nicht gespeicherter Stand des Filters einer Liste. Solange die Seite einen Entwurf hält, kommen die Treffer von
 *  `filterApi.treffer` statt von der gespeicherten Smart View bzw. der Leads-Liste. */
export interface FilterEntwurf {
  s_query: Abfrage;
}

export interface SmartViewNeu {
  name: string;
  type?: FilterObjekt;
  s_query?: Abfrage | null;
  sort?: unknown[] | null;
  selected_fields?: SmartViewFeld[] | null;
  /** Teilen braucht das Close-Recht `manage_team_smart_views` */
  is_shared?: boolean;
  /** Smart View, aus der diese entstanden ist ("Save as") */
  kopie_von?: string | null;
}

export interface SmartViewAenderung {
  name?: string;
  s_query?: Abfrage | null;
  sort?: unknown[] | null;
  selected_fields?: SmartViewFeld[] | null;
  is_shared?: boolean;
}

/** Art eines Feldes im Katalog (API.md 20.1). */
export type FilterFeldArt = "status" | "nutzer" | "wahl" | "enum" | "zeit" | "datum" | "zahl" | "dauer" | "text" | "volltext";
/** Bedingungsarten in der Sprache des Editors (Close: verweis = reference, begriff = term, zeitraum = moment_range). */
export type FilterBedingungsArt = "verweis" | "begriff" | "zeitraum" | "zahl" | "dauer" | "text" | "existiert" | "volltext";

export interface FilterOption {
  wert: string;
  label: string;
  farbe: string | null;
  unter: string | null;
}

/** Feld im Abfragebaum: `{type: "regular_field", object_type, field_name}` oder `{type: "custom_field", custom_field_id}`. */
export type FilterFeldRef = { type: "regular_field"; object_type: string; field_name: string } | { type: "custom_field"; custom_field_id: string };

export interface FilterKatalogFeld {
  id: string;
  label: string;
  art: FilterFeldArt;
  feld: FilterFeldRef | null;
  bedingungen: FilterBedingungsArt[];
  referenz: string | null;
  optionen: FilterOption[] | null;
  einheit: string | null;
  sortierbar: boolean;
  /** Grund, warum sich nach dem Feld (noch) nicht filtern lässt; sonst null */
  deaktiviert: string | null;
  hinweis?: string;
}

export interface FilterKategorie {
  id: string;
  label: string;
  felder: FilterKatalogFeld[];
}

/** Katalog einer Objektart: Felder je Kategorie, verwandte Objekte ("Hat eine Opportunity, bei der …") und sortierbare Felder. */
export interface FilterKatalog {
  objekt: FilterObjekt;
  kategorien: FilterKategorie[];
  verwandt: { id: string; label: string; objekt: FilterObjekt; kategorien: FilterKategorie[] }[];
  sortierfelder: FilterKatalogFeld[];
}

export interface FilterTrefferEingabe {
  s_query: Abfrage;
  limit?: number;
  cursor?: string | null;
}

export const filterApi = {
  /** Treffer einer Abfrage ohne gespeicherte Smart View (Engine, kein Close). Antwort wie `smartViewsApi.treffer`. */
  treffer: (eingabe: FilterTrefferEingabe) => http.post<LeadListe>("/api/filter/treffer", eingabe),
  anzahl: (s_query: Abfrage) => http.post<{ total: number }>("/api/filter/anzahl", { s_query }),
  katalog: (objekt: FilterObjekt = "lead") => http.get<FilterKatalog>("/api/filter/katalog", { objekt }),
};

export const smartViewsSchreibenApi = {
  /** "Save as": neue Smart View (id `save_crm_…`) */
  neu: (eingabe: SmartViewNeu) => http.post<{ smart_view: SmartView }>("/api/smart-views", eingabe),
  /** "Save", Umbenennen, Teilen */
  aendern: (viewId: string, aenderung: SmartViewAenderung) =>
    http.patch<{ smart_view: SmartView }>(`/api/smart-views/${encodeURIComponent(viewId)}`, aenderung),
  loeschen: (viewId: string) => http.del<void>(`/api/smart-views/${encodeURIComponent(viewId)}`),
};

export type { SmartViewKurz };
